import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { Appointment } from '@tailoring-crm/shared';
import { useSetAppointmentStatus } from '../../lib/appointments/use-appointments.js';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  useToast,
} from '@tailoring-crm/ui';

/**
 * They are not coming in — and why, if anybody knows.
 *
 * The reason is optional for the same reason a rejected quotation's is: a shop told "she has to
 * travel for a wedding" should be able to keep that, and a shop whose customer simply went quiet
 * must not be made to invent something before the slot can be freed. It is sent ONLY alongside
 * `status: 'cancelled'` — the shared schema refuses a reason on any other status, so this dialog
 * never has to worry about it leaking onto a different write.
 */
export function CancelAppointmentDialog({
  appointment,
  onOpenChange,
}: {
  /** The booking being cancelled, or null when the dialog is closed. */
  appointment: Appointment | null;
  onOpenChange: (open: boolean) => void;
}): ReactElement {
  const { t } = useTranslation();
  const { toast } = useToast();
  const setStatus = useSetAppointmentStatus();
  const [reason, setReason] = useState('');

  const close = (): void => {
    setReason('');
    onOpenChange(false);
  };

  const submit = (): void => {
    if (!appointment) return;
    void (async () => {
      try {
        await setStatus.mutateAsync({
          appointmentId: appointment.id,
          input: { status: 'cancelled', cancelReason: reason.trim() || null },
        });
        toast({ title: t('appointments.toast.cancelled'), variant: 'success' });
        close();
      } catch (error) {
        toast({
          title: error instanceof Error ? error.message : t('error.unexpected'),
          variant: 'destructive',
        });
      }
    })();
  };

  return (
    <Dialog open={appointment !== null} onOpenChange={(open) => (open ? undefined : close())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {t('appointments.cancel.title', { name: appointment?.customerName ?? '' })}
          </DialogTitle>
          <DialogDescription>{t('appointments.cancel.hint')}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="appointment-cancel-reason">{t('appointments.cancel.reasonLabel')}</Label>
          <Input
            id="appointment-cancel-reason"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder={t('appointments.cancel.reasonPlaceholder')}
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={close}>
            {t('appointments.cancel.keep')}
          </Button>
          <Button variant="destructive" disabled={setStatus.isPending} onClick={submit}>
            {t('appointments.cancel.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default CancelAppointmentDialog;
